// ============================================================================
// MessageBubble — Individual chat message rendering
// ============================================================================

import { useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { beoflowColors, typography, spacing, borderRadius, layout } from '@/theme/muiTheme'
import { useThemeMode } from '@/contexts/ThemeContext'
import { useUserProfile } from '@/services/api/settings'
import { WidgetSandbox } from '@/lib/widget-sandbox'
import { renderMarkdown } from './markdown'
import AttachmentChip from './AttachmentChip'
import ToolCallCard from './ToolCallCard'
import { CardRenderer } from './cards'
import type { ChatMessage, ToolCall, WidgetPreview } from '@/hooks/useChat'

interface MessageBubbleProps {
  message: ChatMessage
  /** True when this is the last message in the conversation — message actions
   *  are then always visible (otherwise hover-revealed on desktop). */
  isLast?: boolean
  /** Re-send the last user message, replacing this assistant turn. */
  onRegenerate?: () => void
}

function WidgetPreviewCard({ preview }: { preview: WidgetPreview }) {
  const label = preview.action === 'created' ? 'Created' : 'Updated'

  return (
    <Box
      sx={{
        my: spacing.xs,
        borderRadius: borderRadius.medium,
        overflow: 'hidden',
        border: `1px solid ${beoflowColors.meanBlue}30`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.xs,
          px: spacing.sm,
          py: '4px',
          backgroundColor: `${beoflowColors.meanBlue}15`,
        }}
      >
        <Typography
          variant="monoSmall"
          sx={{ color: beoflowColors.meanBlue, fontSize: typography.fontSizeTiny }}
        >
          {label}:
        </Typography>
        <Typography
          variant="monoSmall"
          sx={{
            color: 'text.primary',
            fontSize: typography.fontSizeTiny,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {preview.title}
        </Typography>
      </Box>
      <Box sx={{ minHeight: 100 }}>
        <WidgetSandbox
          code={preview.code}
          widgetId={preview.widgetId}
          context="chat"
        />
      </Box>
    </Box>
  )
}

// Quiet mono action label (Copy / Regenerate) under assistant turns.
const actionLabelSx = {
  fontFamily: typography.fontFamilyMonospace,
  fontSize: '10px',
  textTransform: 'uppercase' as const,
  letterSpacing: '1px',
  color: 'text.secondary',
  cursor: 'pointer',
  '&:hover': { color: 'text.primary' },
}

function MessageActions({ message, onRegenerate }: { message: ChatMessage; onRegenerate?: () => void }) {
  const [copied, setCopied] = useState(false)
  const copyTimerRef = useRef<number | null>(null)

  const handleCopy = async () => {
    try {
      // Assistant prose only — tool pills / cards are not part of the copy.
      await navigator.clipboard.writeText(message.content)
      setCopied(true)
      if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current)
      copyTimerRef.current = window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard unavailable (permissions/insecure context) — stay quiet.
    }
  }

  return (
    <Box sx={{ display: 'flex', gap: spacing.md, mt: '4px', px: spacing.xs }}>
      {message.content && (
        <Typography
          component="span"
          onClick={handleCopy}
          sx={{ ...actionLabelSx, ...(copied ? { color: beoflowColors.tealBase } : {}) }}
        >
          {copied ? '✓ Copied' : 'Copy'}
        </Typography>
      )}
      {onRegenerate && (
        <Typography component="span" onClick={onRegenerate} sx={actionLabelSx}>
          Regenerate
        </Typography>
      )}
    </Box>
  )
}

export default function MessageBubble({ message, isLast = false, onRegenerate }: MessageBubbleProps) {
  const isUser = message.role === 'user'
  const { mode } = useThemeMode()
  const isLight = mode === 'light'
  const { data: profile } = useUserProfile()
  const isStaff = profile?.is_staff === true

  // A completed tool call whose result carried a render yields its pill to the
  // card; staff keep the pill for the raw args/result debug view.
  const visibleToolCalls = (message.toolCalls ?? []).filter(
    (tc: ToolCall) => tc.status === 'calling' || !tc.hasRender || isStaff
  )
  const hasToolCalls = visibleToolCalls.length > 0

  // Chart-render widgets mount via InsightChartCard (CardRenderer), so the
  // plain preview card only shows widgets that did NOT come from a render.
  const previewWidgets = (message.widgets ?? []).filter(wp => !wp.fromRender)

  const showActions = Boolean(
    !isUser && !message.isStreaming && (message.content || message.renders?.length)
  )

  // The assistant bubble is the CARD surface: a quiet translucent step off the
  // canvas, not a filled slab. It was a near-white #F0F0F0 in light and an
  // opaque grey in dark, which made Flow the loudest object on any screen it
  // shares with the work it is talking about. The coach's own bubble keeps the
  // Mean Blue tint, so the two speakers still read apart. Approved app-wide by
  // Daniel, 2026-09-18: this changes the main chat page too.
  const bubbleStyle = isUser
    ? { backgroundColor: `${beoflowColors.meanBlue}29`, borderRadius: borderRadius.medium }
    : {
        backgroundColor: isLight
          ? `${beoflowColors.beoBlack}0D`
          : `${beoflowColors.beoWhite}0D`,
        borderRadius: borderRadius.medium,
      }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        mb: spacing.md,
        px: spacing.sm,
        // Hover reveals the quiet action labels on desktop.
        '&:hover .msg-actions': { opacity: 1 },
      }}
    >
      <Box
        sx={{
          ...bubbleStyle,
          maxWidth: { xs: '90%', sm: '75%', md: '70%' },
          p: layout.chatBubblePadding,
          // Streaming cursor
          ...(message.isStreaming && !message.content && !hasToolCalls
            ? {
                '&::after': {
                  content: '"\\25CF \\25CF \\25CF"',
                  fontFamily: typography.fontFamilyMonospace,
                  fontSize: typography.fontSizeTiny,
                  color: 'text.secondary',
                  letterSpacing: '2px',
                  animation: 'pulse 1.4s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 0.3 },
                    '50%': { opacity: 1 },
                  },
                },
              }
            : {}),
        }}
      >
        {/* Who is speaking. The coach knows which bubble is theirs from the
            side and the tint; Flow is named, because on a surface where it is
            editing the coach's own work that is worth being explicit about. */}
        {!isUser && (
          <Typography
            sx={{
              fontFamily: typography.fontFamilyMonospace,
              fontSize: '9px',
              letterSpacing: typography.letterLabel,
              textTransform: 'uppercase',
              color: 'text.disabled',
              display: 'block',
              mb: '3px',
            }}
          >
            Flow
          </Typography>
        )}

        {/* Tool calls (pills stay for in-progress and render-less results) */}
        {hasToolCalls &&
          visibleToolCalls.map((tc, idx) => (
            <ToolCallCard key={`${tc.tool}-${idx}`} toolCall={tc} isStaff={isStaff} />
          ))}

        {/* Widget previews (dormant non-render path) */}
        {previewWidgets.map((wp) => (
          <WidgetPreviewCard key={`widget-${wp.widgetId}-${wp.action}`} preview={wp} />
        ))}

        {/* Message content */}
        {message.content && (
          <Box>
            {isUser ? (
              <Typography variant="body1">{message.content}</Typography>
            ) : (
              renderMarkdown(message.content)
            )}
          </Box>
        )}

        {/* Render cards — after the prose, in stream order */}
        {message.renders?.map((render, idx) => (
          <CardRenderer
            key={`render-${render.type}-${idx}`}
            render={render}
            isStaff={isStaff}
            live={message.isStreaming === true}
            widgets={message.widgets}
          />
        ))}

        {/* Attachments (user messages) */}
        {message.attachments && message.attachments.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: spacing.xs,
              mt: message.content ? spacing.sm : 0,
            }}
          >
            {message.attachments.map((att, i) => (
              <AttachmentChip
                key={`${att.filename}-${i}`}
                filename={att.filename}
                mediaType={att.media_type}
                onBubble={isUser}
              />
            ))}
          </Box>
        )}

        {/* Streaming cursor after content */}
        {message.isStreaming && message.content && (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              width: '6px',
              height: '16px',
              backgroundColor: isUser ? '#FFF' : 'text.primary',
              ml: '2px',
              verticalAlign: 'text-bottom',
              animation: 'blink 1s step-end infinite',
              '@keyframes blink': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0 },
              },
            }}
          />
        )}
      </Box>

      {/* Message actions: hover-visible on desktop, always on the last turn */}
      {showActions && (
        <Box
          className="msg-actions"
          sx={{
            opacity: isLast ? 1 : 0,
            transition: 'opacity 150ms ease',
            // Touch devices have no hover — keep the actions visible.
            '@media (hover: none)': { opacity: 1 },
          }}
        >
          <MessageActions message={message} onRegenerate={isLast ? onRegenerate : undefined} />
        </Box>
      )}
    </Box>
  )
}
